/*
 * Copyright (c) 2026 Brainstream Lab — UCogNet Research.
 * Licensed under the Apache License, Version 2.0 (see LICENSE / NOTICE).
 */
import React from 'react';
import { Box, Container, Typography, IconButton, Link } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { useI18n } from '../i18n/I18nContext';

const enlace = {
  color: colors.textSecondary, fontSize: '0.78rem',
  textDecoration: 'none', '&:hover': { color: colors.accent },
};

export default function Footer() {
  const { t } = useI18n();
  return (
    <Box component="footer" sx={{
      py: 5,
      borderTop: `1px solid ${colors.border}`,
      background: colors.surface,
    }}>
      <Container maxWidth="lg" sx={{
        display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'center', md: 'flex-start' },
        justifyContent: 'space-between', gap: 3,
      }}>
        {/* Identidad */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, alignItems: { xs: 'center', md: 'flex-start' } }}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
            &copy; {new Date().getFullYear()} U-CogNet &mdash; Brainstream Lab.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" sx={{
              fontSize: '0.68rem', fontWeight: 500,
              color: colors.textSecondary, letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              {t('footer.product_of')}
            </Typography>
            <Box
              component="img"
              src="/logo-brainstream-blanco.png"
              alt="Brainstream"
              sx={{
                height: 30, width: 'auto', display: 'block',
                opacity: 0.7, transition: 'opacity 0.3s ease', '&:hover': { opacity: 1 },
              }}
            />
          </Box>
        </Box>

        {/* Enlaces */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: { xs: 'center', md: 'flex-start' } }}>
          <Link component={RouterLink} to="/leadership" sx={enlace}>{t('footer.leadership')}</Link>
          <Link component={RouterLink} to="/contact" sx={enlace}>{t('footer.contacto')}</Link>
        </Box>

        {/* Contacto */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'center', md: 'flex-end' } }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { icon: <LinkedInIcon fontSize="small" />, href: 'https://linkedin.com/company/brainstream-pro', label: 'LinkedIn' },
              { icon: <EmailIcon fontSize="small" />, href: 'mailto:samuel@ucognet.pro', label: 'Email' },
            ].map((s) => (
              <IconButton key={s.label}
                component="a" href={s.href} target="_blank" rel="noopener noreferrer"
                aria-label={s.label} title={s.label}
                sx={{ color: colors.textSecondary, '&:hover': { color: colors.accent } }}
              >
                {s.icon}
              </IconButton>
            ))}
          </Box>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>
            <Link href="mailto:samuel@ucognet.pro" sx={{ color: colors.textSecondary, '&:hover': { color: colors.accent } }}>
              samuel@ucognet.pro
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
